import { useEffect } from 'react';
import type { BannerDefinition } from '../banner.types';
import { useBannerActions } from './useBanners';

/**
 * Registers or removes a banner based on a boolean condition.
 * When `active` is true the banner is added; when false (or on unmount) it is removed.
 *
 * @internal Prefer {@link useBannerRegistration} outside this module — it
 * memoizes the definition so `definition` stays referentially stable across
 * renders. Using this hook directly with a fresh object literal re-triggers
 * the underlying effect on every render.
 */
export function useConditionalBanner(active: boolean, definition: BannerDefinition) {
  const { addBanner, removeBanner } = useBannerActions();

  useEffect(() => {
    if (!active) {
      removeBanner(definition.id);
      return;
    }

    addBanner(definition);

    return () => removeBanner(definition.id);
  }, [active, definition, addBanner, removeBanner]);
}
